/* ============================================================
   Image Text Carousel — image-text-carousel.js
   ============================================================ */
(function () {
  'use strict';

  const DURATION = 500; // ms — must align with CSS transition-duration
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ImageTextCarousel {
    constructor(el) {
      this.el    = el;
      this.track = el.querySelector('.itc__track');
      this.slides = Array.from(el.querySelectorAll('.itc__slide'));
      this.dots   = Array.from(el.querySelectorAll('.itc__seg'));
      this.prevBtn = el.querySelector('.itc__nav-btn--prev');
      this.nextBtn = el.querySelector('.itc__nav-btn--next');

      this.total   = this.slides.length;
      this.current = 0;
      this.anim    = (this.track && this.track.dataset.animation) || 'fade';
      this.freq    = parseInt((this.track && this.track.dataset.frequency) || '0', 10) * 1000;
      this.busy    = false;
      this.timer   = null;
      this._touchStartX = 0;

      this._init();
    }

    /* ---- Bootstrap ---- */

    _init() {
      // Keyword highlighting rewrites DOM — defer to idle time so it
      // never blocks the critical rendering path on first load.
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this._highlightKeywords(), { timeout: 1500 });
      } else {
        setTimeout(() => this._highlightKeywords(), 0);
      }

      if (this.total < 2) return;
      this._bindEvents();
      this._setupObserver();
    }

    /* ---- Intersection Observer — autoplay only while visible ---- */

    _setupObserver() {
      // No IntersectionObserver support (very old browsers): fall back to immediate start
      if (!('IntersectionObserver' in window)) {
        if (this.freq > 0 && !reducedMotion) this._startAutoplay();
        return;
      }

      this._io = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          // Section entered viewport: start autoplay
          if (this.freq > 0 && !reducedMotion) this._startAutoplay();
        } else {
          // Section left viewport: pause to save CPU / battery
          this._stopAutoplay();
        }
      }, { threshold: 0.2 });

      this._io.observe(this.el);
    }

    /* ---- Keyword Highlighting ---- */

    _highlightKeywords() {
      this.slides.forEach(slide => {
        slide.querySelectorAll('[data-keywords]').forEach(element => {
          const raw = element.dataset.keywords || '';
          if (!raw.trim()) return;

          const italic   = element.dataset.kwItalic === 'true';
          const underline = element.dataset.kwUnderline === 'true';
          const weight   = element.dataset.kwWeight || '400';
          const color    = element.dataset.kwColor  || '';

          const keywords = raw.split(',').map(k => k.trim()).filter(Boolean);
          if (!keywords.length) return;

          const escaped = keywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
          const regex   = new RegExp(`(${escaped.join('|')})`, 'gi');

          const styles = [`font-weight:${weight}`];
          if (italic)   styles.push('font-style:italic');
          if (underline) styles.push('text-decoration:underline');
          if (color)    styles.push(`color:${color}`);
          const styleAttr = styles.join(';');

          const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
          const textNodes = [];
          let textNode;

          while ((textNode = walker.nextNode())) textNodes.push(textNode);

          textNodes.forEach(node => {
            const fragment = document.createDocumentFragment();
            const text = node.nodeValue;
            let lastIndex = 0;
            let match;

            regex.lastIndex = 0;
            while ((match = regex.exec(text))) {
              fragment.append(document.createTextNode(text.slice(lastIndex, match.index)));

              const mark = document.createElement('mark');
              mark.className = 'itc__kw';
              mark.style.cssText = styleAttr;
              mark.textContent = match[0];
              fragment.append(mark);
              lastIndex = match.index + match[0].length;
            }

            if (lastIndex) {
              fragment.append(document.createTextNode(text.slice(lastIndex)));
              node.replaceWith(fragment);
            }
          });
        });
      });
    }

    /* ---- Events ---- */

    _bindEvents() {
      this.prevBtn?.addEventListener('click', () => this._prev());
      this.nextBtn?.addEventListener('click', () => this._next());
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this._goTo(i)));


      // Touch swipe
      this.track.addEventListener('touchstart', e => {
        this._touchStartX = e.touches[0].clientX;
      }, { passive: true });
      this.track.addEventListener('touchend', e => {
        const delta = this._touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(delta) > 48) delta > 0 ? this._next() : this._prev();
      });

      // Keyboard
      this.el.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this._prev(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this._next(); }
      });
    }

    /* ---- Navigation ---- */

    _next() { this._goTo((this.current + 1) % this.total); }
    _prev() { this._goTo((this.current - 1 + this.total) % this.total); }

    _goTo(index) {
      if (this.busy || index === this.current) return;
      this.busy = true;

      const from = this.slides[this.current];
      const to   = this.slides[index];
      const dir  = index > this.current ? 'next' : 'prev';
      this.current = index;

      this._transition(from, to, dir);
      this._updateDots(index);
      this._updateAria(from, to);
    }

    /* ---- Transition dispatcher ---- */

    _transition(from, to, dir) {
      // Honour OS-level "reduce motion" preference — instant switch, no animation
      if (reducedMotion) { this._fadeTransition(from, to); return; }

      switch (this.anim) {
        case 'slide': this._swipeTransition(from, to, dir); break;
        case 'swap':  this._scaleTransition(from, to, 'scale(0.88)', 'scale(1.08)'); break;
        case 'zoom':  this._scaleTransition(from, to, 'scale(1.08)', 'scale(0.94)'); break;
        default:      this._fadeTransition(from, to); break;
      }
    }

    /* ── Fade ──────────────────────────────────────────────── */
    _fadeTransition(from, to) {
      from.classList.remove('itc__slide--active');
      to.classList.add('itc__slide--active');
      this._cleanup(from);
    }

    /* ── Swipe (true horizontal pan, no opacity bleed) ─────── */
    _swipeTransition(from, to, dir) {
      const enterFrom = dir === 'next' ? '100%' : '-100%';
      const leaveTo   = dir === 'next' ? '-100%' : '100%';
      const ease      = `transform ${DURATION}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;

      // Step 1 — kill ALL transitions on both slides so nothing fires during setup
      this._freeze(from, 'translateX(0)',        '1');
      this._freeze(to,   `translateX(${enterFrom})`, '2');

      // Swap active class while frozen — CSS opacity/visibility changes are instant (no transition)
      from.classList.remove('itc__slide--active');
      to.classList.add('itc__slide--active');

      // Step 2 — single reflow: browser commits the frozen positions
      void to.offsetWidth;

      // Step 3 — enable transform-only transition (opacity stays instant to avoid bleed)
      from.style.transition = ease;
      to.style.transition   = ease;

      // Step 4 — set target positions; browser animates from frozen → target
      from.style.transform = `translateX(${leaveTo})`;
      to.style.transform   = 'translateX(0)';

      // Step 5 — clean up both after animation finishes
      this._cleanup(from);
      this._cleanup(to, false); // false = don't mark busy done twice
    }

    /* Freeze a slide at a given transform with no transition and full opacity */
    _freeze(el, transform, zIndex) {
      el.style.transition   = 'none';
      el.style.opacity      = '1';
      el.style.visibility   = 'visible';
      el.style.pointerEvents = 'none';
      el.style.transform    = transform;
      el.style.zIndex       = zIndex;
    }

    /* ── Scale (swap / zoom) ───────────────────────────────── */
    _scaleTransition(from, to, toInitScale, fromExitScale) {
      // Prepare incoming slide: invisible, scaled, no transition
      to.style.transition  = 'none';
      to.style.opacity     = '0';
      to.style.visibility  = 'visible';
      to.style.transform   = toInitScale;
      to.style.zIndex      = '2';

      void to.offsetWidth;

      // Enable transition on incoming, animate to natural state
      to.style.transition = `opacity ${DURATION}ms ease, transform ${DURATION}ms ease`;
      to.style.opacity    = '1';
      to.style.transform  = '';

      // Outgoing: transition out
      from.style.transition = `opacity ${DURATION}ms ease, transform ${DURATION}ms ease`;
      from.style.transform  = fromExitScale;
      from.style.opacity    = '0';
      from.classList.remove('itc__slide--active');
      to.classList.add('itc__slide--active');

      this._cleanup(from);
      this._cleanup(to, false);
    }

    /* ── Shared cleanup ────────────────────────────────────── */
    _cleanup(el, markDone = true) {
      setTimeout(() => {
        // 1. Kill transitions so style resets are instant
        el.style.transition = 'none';
        void el.offsetWidth;
        // 2. Clear all inline overrides — CSS class state takes over cleanly
        el.style.opacity      = '';
        el.style.visibility   = '';
        el.style.transform    = '';
        el.style.zIndex       = '';
        el.style.pointerEvents = '';
        void el.offsetWidth;
        // 3. Restore CSS transitions
        el.style.transition   = '';
        if (markDone) this.busy = false;
      }, DURATION + 40);
    }

    /* ---- Segments + Counter + ARIA ---- */

    _updateDots(active) {
      this.dots.forEach((seg, i) => {
        const on = i === active;
        seg.classList.toggle('itc__seg--active', on);
        seg.setAttribute('aria-selected', String(on));

        const fill = seg.querySelector('.itc__seg-fill');
        if (!fill) return;

        if (on) {
          // Animate fill across the autoplay duration; instant if no autoplay
          fill.style.transition = 'none';
          fill.style.width = '0%';
          void fill.offsetWidth; // flush so reset registers
          if (this.freq > 0 && !reducedMotion) {
            fill.style.transition = `width ${this.freq}ms linear`;
          }
          fill.style.width = '100%';
        } else {
          fill.style.transition = 'none';
          fill.style.width = '0%';
        }
      });
    }

    _updateAria(from, to) {
      from.setAttribute('aria-hidden', 'true');
      to.setAttribute('aria-hidden', 'false');
    }

    /* ---- Autoplay ---- */

    _startAutoplay() {
      this._stopAutoplay();
      this.timer = setInterval(() => this._next(), this.freq);
      // Re-sync fill animation for the active segment
      this._updateDots(this.current);
    }

    _stopAutoplay() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
      // Freeze fill in place when paused
      const activeSeg = this.dots[this.current];
      if (activeSeg) {
        const fill = activeSeg.querySelector('.itc__seg-fill');
        if (fill) {
          const computed = getComputedStyle(fill).width;
          fill.style.transition = 'none';
          fill.style.width = computed;
        }
      }
    }
  }

  /* ---- Page bootstrap ---- */

  function initAll() {
    document.querySelectorAll('.itc:not([data-itc-ready])').forEach(el => {
      el.setAttribute('data-itc-ready', '');
      new ImageTextCarousel(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor live-reload
  document.addEventListener('shopify:section:load', e => {
    const el = e.target.querySelector('.itc');
    if (el) { el.removeAttribute('data-itc-ready'); new ImageTextCarousel(el); }
  });
  document.addEventListener('shopify:section:select', e => {
    e.target.querySelector('.itc__slide--active')?.scrollIntoView({ block: 'nearest' });
  });
})();
